import React, { useContext, useEffect, useState } from 'react';
import { Grid, Typography, Box, Button, Card, CardContent, Chip, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../Helpers/Contexts/authContext';
import { AuthUser } from '../../../interfaces/Contexts';
import { getBusinesses } from '../../../Utils/apiService';
import routes from '../../../Utils/routes';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';

const BusinessesContent: React.FC = () => {
  const navigate = useNavigate();
  const {
    state: { user }
  } = useContext(AuthContext);
  const userData = user as AuthUser;
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log("Fetching businesses for applicantId:", userData?.applicantId);
    setLoading(true);

    getBusinesses(userData?.applicantId)
      .then((res) => {
        console.log("Business data response:", res?.data?.data?.myBusinesses);
        const businessList = res?.data?.data?.myBusinesses || [];
        setBusinesses(businessList);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching businesses:", err);
        setBusinesses([]);
        setLoading(false);
      });
  }, [userData?.applicantId]);

  const handleCreateBusiness = () => {
    navigate(routes.createbusinessdirectory);
  };

  const handleViewMore = () => {
    navigate(routes.businessdirectory);
  };

  const handleBusinessClick = (businessId: string) => {
    navigate(`${routes.businessdirectory}/${businessId}`);
  };

  const renderBusinessCard = (business: any, index: number) => (
    <Card
      key={index}
      sx={{
        cursor: 'pointer',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        backgroundColor: '#f9f9f9',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        },
        position: 'relative',
        height: { xs: 'auto', sm: '200px' }
      }}
      onClick={() => handleBusinessClick(business.Id)}
    >
      <CardContent sx={{ 
        p: { xs: 2, sm: 3 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          position: 'absolute', 
          top: { xs: 12, sm: 16 }, 
          right: { xs: 12, sm: 16 },
          zIndex: 1
        }}>
          <Chip
            label={business.Business_Approval__c || 'In review'}
            size="small"
            sx={{
              borderRadius: '16px',
              fontSize: '12px',
              backgroundColor: '#FFF2D1',
              color: '#9E7D27',
              fontWeight: 500
            }}
          />
        </Box>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          mb: 3,
          mt: { xs: 2, sm: 0 },
          pr: { xs: '60px', sm: '80px' },
          flex: 1
        }}>
          <Box
            sx={{
              width: { xs: 64, sm: 64 },
              height: { xs: 64, sm: 64 },
              minWidth: { xs: 64, sm: 64 },
              minHeight: { xs: 64, sm: 64 },
              borderRadius: '50%',
              backgroundColor: '#1a1a1a',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mr: 2,
              flexShrink: 0
            }}
          >
            {business.Logo_URL__c ? (
              <img
                src={business.Logo_URL__c}
                alt={business.Name}
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <BusinessIcon sx={{ color: 'white', fontSize: 32 }} />
            )}
          </Box>
          <Box sx={{ 
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            overflow: 'hidden'
          }}>
            <Tooltip 
              title={business.Name || 'SpaceMan'} 
              placement="top"
              arrow
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: { xs: '16px', sm: '18px' }, 
                  fontWeight: 600,
                  textAlign: 'left',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.3em',
                  maxHeight: '2.6em'
                }}
              >
                {business.Name || 'SpaceMan'}
              </Typography>
            </Tooltip>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mt: 0.5,
              width: '100%',
              justifyContent: 'flex-start',
              overflow: 'hidden'
            }}>
              <Box component="span" sx={{ mr: 0.5, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 13.5C13.933 13.5 15.5 11.933 15.5 10C15.5 8.067 13.933 6.5 12 6.5C10.067 6.5 8.5 8.067 8.5 10C8.5 11.933 10.067 13.5 12 13.5Z" stroke="#666" strokeWidth="1.5" />
                  <path d="M12 22C12 22 20 16.5 20 10C20 5.5 16.5 2 12 2C7.5 2 4 5.5 4 10C4 16.5 12 22 12 22Z" stroke="#666" strokeWidth="1.5" />
                </svg>
              </Box>
              <Tooltip 
                title={business.Business_Address__c || 'Chennai, India'} 
                placement="top"
                arrow
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666', 
                    fontSize: { xs: '12px', sm: '14px' },
                    textAlign: 'left',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {business.Business_Address__c || 'Chennai, India'}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={{ 
          mt: 'auto',
          display: 'flex', 
          justifyContent: 'flex-start' 
        }}>
          <Chip
            label={business.Business_Type__c || 'Technology'}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: '16px',
              fontSize: '12px',
              border: '1px solid #e0e0e0',
              backgroundColor: 'white',
              color: '#666'
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2 }}>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      ) : businesses.length > 0 ? (
        <>
          <Grid container spacing={2}>
            {businesses.slice(0, 6).map((business: any, index: number) => (
              <Grid key={index} item xs={12} sm={6} md={4}>
                {renderBusinessCard(business, index)}
              </Grid>
            ))}
          </Grid>
          {businesses.length > 6 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleViewMore}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: '12px',
                  fontWeight: 500
                }}
              >
                View More
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
          textAlign: 'center'
        }}>
          <BusinessIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
          <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
            You haven't listed any businesses yet
          </Typography>
       <Button
  variant="contained"
  startIcon={<AddIcon />}
  onClick={handleCreateBusiness}
  sx={{
    borderRadius: '6px',
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: '#2C60AA', // ✅ button color
    color: '#fff',              // ✅ white text
    '&:hover': {
      backgroundColor: '#2C60AA', // ✅ same color on hover
    },
  }}
>
  List Your First Business
</Button>

        </Box>
      )}
    </Box>
  );
};

export default BusinessesContent;
